import { Injectable } from '@angular/core';
import { UserAuth } from '../user/user.auth';
import { TabulatorService } from '../tabulator/tabulator.service';
import { ITabulatorColumns } from '../../models/tabulator';
import { filter, forEach } from 'lodash';
import * as moment from 'moment';
import { TestListTransformationService } from './service/test-list.transformation.service';
import { UserService } from '../user/user.service';
import { AGENT_MODEL, Device, IDeviceData } from '../device/device';
import { LicensesPageService } from '../../services/licenses/licenses.page.service';
import { DateFormatterService } from '@nrwl/common-lib';

export interface ITableObject {
  dimension: string;
  title: string;
  column: Array<ITabulatorColumns | any>; // eslint-disable-line
  data: Array<any>; // eslint-disable-line
}

export enum FUSION_AGENT_ACTION_NAME {
  EDIT = 'Edit',
  REMOVE = 'Remove',
  DOWNLOAD_AGENT = 'Download agent',
  DISABLE = 'Disable',
  ENABLED = 'Enable',
  REBOOT = 'Reboot',
  UPGRADE_AGENT = 'Upgrade agent',
  UPDATE_AGENT = 'Update agent',
  VIEW_TESTS = 'View tests',
  AGENT_SPECS = 'Agent specs',
  TRAFFIC_RESULTS = 'Traffic results',
  VIEW_JMEPS = 'View JMEPs',
  DISCOVER_JMEPS = 'Discover JMEPs',
  ALARMS = 'Alarms',
  CONFIGURE = 'Configure',
  RESTART = 'Restart',
  RESYNC = 'Resync',
  UPGRADE_JMEPS = 'Upgrade',
  STOP = 'Stop loopback and RFC-6349 sessions',
  TRANSFER_DEVICE = 'Transfer Device',
  LOGS = 'Logs'
}
export enum FUSION_TEMPLATE_ACTION_NAME {
  OPEN = 'Open',
  SET_AS_DEFAULTS = 'Set as test defaults',
  UNSET_AS_DEFAULTS = 'Unset as test defaults',
  SHARE = 'Share',
  UNSHARE = 'Unshare',
  REMOVE = 'Remove'
}

@Injectable({
  providedIn: 'root'
})
export class FusionTransformationService {
  deviceList: object;
  jmepActions: string[] = [
    'agentspecs',
    'remove',
    'alarms',
    'configure',
    'restart',
    'resync',
    'stopLoopback',
    'trafficResults',
    'transferDevice',
    'jmep-upgrade',
    'viewtests'
  ];
  vpmaActions: string[] = ['agentspecs', 'edit', 'remove', 'reboot', 'viewtests'];
  esamActions: string[] = ['viewJmeps', 'agentspecs', 'edit', 'remove', 'discoverJmeps'];
  commonDeviceActions: string[] = ['agentspecs', 'edit', 'remove', 'viewtests'];
  vqtActions: string[] = ['agentspecs', 'edit', 'reboot', 'upgrade', 'remove', 'viewtests'];
  qt600Actions: string[] = ['agentspecs', 'edit', 'remove', 'viewtests', 'upgrade'];
  map2100Actions: string[] = ['agentspecs', 'edit', 'remove', 'viewtests', 'upgrade'];
  vmaActions: string[] = ['agentspecs', 'edit', 'remove', 'viewtests', 'upgrade'];
  vspaActions: string[] = ['agentspecs', 'edit', 'remove', 'viewtests', 'upgrade', 'logs'];

  constructor(
    private readonly userAuth: UserAuth,
    private readonly tabulatorService: TabulatorService,
    private readonly testListTransform: TestListTransformationService,
    private readonly userService: UserService,
    private readonly licenseService: LicensesPageService,
    private readonly dateFormatService: DateFormatterService
  ) {}

  // eslint-disable-next-line
  transformData(
    type: any,
    data: any,
    historicalMode = false,
    dynamicColumn?,
    metricsUnit?
  ): ITableObject {
    const template = this.getTemplate(type, metricsUnit);
    const temp = [];
    forEach(data, item => {
      temp.push(this.getTableObject(type, item, historicalMode, dynamicColumn));
    });

    template.data = temp;
    return template;
  }

  private getTableObject(dimension, entity, historicalMode?, dynamicColumn?) {
    let result: any = {}; // eslint-disable-line

    switch (dimension) {
      case 'test-list-data': {
        const stateActions = this.testListTransform.getTestStateActions(entity);
        result = {
          id: entity.getId(),
          runType: this.testListTransform.getSchedulingInfo(entity),
          name: this.testListTransform.getTestNameInfo(entity),
          state: this.testListTransform.getTestStatus(entity),
          location: this.testListTransform.getAgentInfo(entity, false),
          updated: this.testListTransform.getUpdatedDate(entity),
          destination: this.testListTransform.getAgentInfo(entity, true),
          tests: this.testListTransform.getMonitorTestType(entity),
          actions: !entity.isDeleted ? stateActions : [],
          isCheckboxDisabled: entity.isCheckboxDisabled,
          runOnce: entity.isRunOnceTest(),
          expiryDate: this.testListTransform.getExpiryDate(entity)
        };
        if (dynamicColumn) {
          dynamicColumn.forEach(item => {
            result[item.tabulatorInfo.field] = entity[item.tabulatorInfo.field];
          });
        }
        break;
      }
      case 'datasource-management': {
        entity.version.label =
          entity.version.label.toLowerCase() === 'unknown' ? '-' : entity.version.label;
        result = {
          ipAddress: entity.ipAddress ?? '-',
          mgmtIpAddress: entity.mgmtIpAddress ?? '-',
          id: entity.id,
          name: entity.name,
          type: entity.type,
          site: entity.site,
          status: entity.status,
          location: entity.location,
          version: entity.version,
          isDisabled: entity.isDisabled,
          registered: this.getISODateTimeFormat(entity.registeredState),
          testAssigned: entity.testAssigned,
          dataItem: entity.dataItem,
          hasAgentUrl: entity.hasAgentUrl,
          dataType: entity.dataType,
          actions: entity.actions,
          isCheckboxDisabled: entity.isDisabled,
          _children: entity.children
        };

        break;
      }
      case 'endpoints-management': {
        result = {
          ipAddress: entity.ipAddress ?? '-',
          id: entity.id,
          name: entity.name,
          type: entity.type,
          status: entity.status,
          testAssigned: entity.testAssigned,
          dataItem: entity.dataItem,
          dataType: entity.dataType,
          reflectorData: entity.reflectorData,
          actions: entity.actions,
          emitOnClick: true,
          _children: entity.children
        };

        break;
      }
      case 'templates-management': {
        result = {
          id: entity.id,
          name: entity.name,
          type: { label: this.testListTransform.getTestType(entity.testType) },
          owner: this.capitalizeFirstLetter(entity.user),
          testType: entity.testType,
          accessLevel: entity.isDefault
            ? 'Default'
            : this.capitalizeFirstLetter(entity.accessLevel),
          lastModified: this.getISODateTimeFormat(entity.timestamp),
          actions: this.getTemplateActions(entity)
        };

        break;
      }
      case 'device-selection': {
        result = {
          id: entity.id,
          name: {
            label: !entity.phyId
              ? entity.name.label
              : `${entity.name.label} - ${entity.phyId.toUpperCase()}`
          },
          status: entity.dataItem.status,
          type: {
            label: entity.dataItem.model.model
          },
          ip: entity.ipAddress,
          mgmtIp: entity.mgmtIPaddress,
          itemType: 'device',
          subLabel: entity.dataItem.model.model,
          isChecked: entity.isChecked ? entity.isChecked : false,
          dataItem: entity.dataItem,
          location: entity.location,
          testAssigned: entity.testAssigned,
          isCheckboxDisabled: entity.warningMessage ? true : false,
          isWarningMessage: entity.warningMessage ? true : false,
          warningMessage: entity.warningMessage
        };
        break;
      }
      case 'jmeps-discovery': {
        result = {
          id: entity.ipAddress,
          ipAddress: entity.ipAddress,
          probeId: entity.probeId,
          equipmentMac: entity.equipmentMac,
          announced: entity.announced,
          activateStatus: entity.activateStatus,
          processState: entity.processState,
          ipAssignStatus: entity.ipAssignStatus,
          errorMsg: entity.errorMsg
        };
        break;
      }
      case 'flows-selection': {
        result = {
          id: entity.flowId,
          flowId: entity.flowId,
          srcIp: entity?.srcIp,
          destIP: entity?.destIP,
          srcMac: entity?.srcMac,
          destMac: entity?.destMac,
          megLevel: entity?.megLevel,
          port: entity?.port,
          negPort: entity?.negPort,
          dscp: entity?.dscp,
          nfVal: entity?.nfVal,
          fnVal: entity?.fnVal,
          twVal: entity?.twVal,
          fsync: entity?.fsync,
          pmStats: entity?.pmStats,
          details: entity?.details,
          isChecked: entity?.isChecked ? entity.isChecked : false,
          isCheckboxDisabled: entity?.isChecked ? entity.isChecked : false
        };
        break;
      }
      case 'uploads-settings': {
        const actions = [
          {
            id: 'download',
            option: 'download',
            label: 'Download',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-download',
            isDefaultAction: false
          },
          {
            id: 'remove',
            option: 'remove',
            label: 'Remove',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-delete',
            isDefaultAction: false
          }
        ];
        result = {
          id: entity.id,
          version: entity.version,
          url: entity.url,
          image: entity.image,
          type: entity.type,
          actions: [{ label: '', actions: actions }]
        };
        break;
      }
      case 'downloads-settings': {
        const actions = [
          {
            id: 'download',
            option: 'download',
            label: 'Download',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-download',
            isDefaultAction: false
          },
          {
            id: 'remove',
            option: 'remove',
            label: 'Remove',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-delete',
            isDefaultAction: false
          }
        ];
        result = {
          id: entity.id,
          fileName: entity.fileName,
          type: entity.type,
          filePath: entity.filePath,
          fileModifyTime: entity.fileModifyTime,
          lastAuditTime: entity.lastAuditTime,
          status: entity.status,
          message: entity.message,
          progressStatus: entity.progressStatus,
          resultsTime: entity.resultsTime,
          actions: [{ label: '', actions: actions }]
        };
        break;
      }
      case 'uploads-jmep-mapping': {
        const actions = [
          {
            id: 'apply',
            option: 'apply',
            label: 'Apply',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi mdi-plus',
            isDefaultAction: false
          },
          {
            id: 'download',
            option: 'download',
            label: 'Download Input File',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-download',
            isDefaultAction: false
          },
          {
            id: 'remove',
            option: 'remove',
            label: 'Remove',
            disable: !this.userAuth.isAuthedFor('READ_WRITE'),
            iconName: 'mdi-delete',
            isDefaultAction: false
          },
          {
            id: 'results',
            option: 'results',
            label: 'View Last Results',
            disable: !this.userAuth.isAuthedFor('READ_WRITE') || !entity.resultsTime,
            iconName: 'mdi-format-list-bulleted',
            isDefaultAction: false
          }
        ];
        result = {
          id: entity.id,
          fileName: entity.fileName,
          uploadTime: entity.uploadTime,
          resultsTime: entity.resultsTime,
          status: entity.status,
          actions: [{ label: '', actions: actions }]
        };
        break;
      }
      case 'uploads-view-results': {
        result = {
          id: entity?.id,
          applyTime: entity?.applyTime,
          model: entity?.model,
          key: entity?.key,
          overWrite: entity?.overWrite,
          originalName: entity?.originalName,
          newName: entity?.newName,
          status: entity?.status,
          reason: entity?.reason
        };
        break;
      }
      case 'agent-logs': {
        result = {
          maId: entity.maId,
          agentName: entity.name,
          timestamp: this.getTimestampAndLogURL(entity)
        };
        break;
      }
    }
    return result;
  }
  // eslint-disable-next-line
  getTemplate(type: string, metricsUnit?: string): ITableObject {
    switch (type) {
      case 'test-list-data': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Name',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name.label'
                    },
                    {
                      fields: 'name.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'tests',
                  columnName: 'name'
                },
                width: '16%'
              }
            },
            {
              tabulatorInfo: {
                field: 'tests.label',
                title: 'Test',
                tooltip: false,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  columnName: 'tests',
                  tableName: 'tests'
                },
                headerFilter: true,
                width: '12%'
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'state.label',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  columnName: 'state',
                  tableName: 'state'
                },
                width: '9%'
              },
              extras: {
                isMultiselectFilter: true,
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'location.label',
                title: 'Originator',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'location.label'
                    },
                    {
                      fields: 'location.sublabel'
                    },
                    {
                      fields: 'location.sublabel1'
                    }
                  ]
                },
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  columnName: 'location',
                  tableName: 'type'
                },
                width: '13%'
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'destination.label',
                title: 'Destination',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'destination.label'
                    },
                    {
                      fields: 'destination.sublabel'
                    },
                    {
                      fields: 'destination.sublabel1'
                    }
                  ]
                },
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  columnName: 'destination',
                  tableName: 'type'
                },
                width: '13%'
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'runType.label',
                title: 'Schedule',
                headerFilter: true,
                headerFilterParams: {
                  valuesLookup: true,
                  clearable: true
                },
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'runType.label'
                    },
                    {
                      fields: 'runType.sublabel'
                    },
                    {
                      fields: 'runType.sublabel1'
                    }
                  ]
                },
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'tests',
                  columnName: 'runType'
                },
                width: '9%'
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'updated.value',
                title: 'Updated (UTC)',
                id: 'updated',
                headerFilter: true,
                sorter: 'number',
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'updated.label'
                    },
                    {
                      fields: 'updated.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'updated'
                },
                width: '8%'
              }
            },
            {
              tabulatorInfo: {
                field: 'expiryDate.value',
                title: 'Expires (UTC)',
                id: 'expiryDate',
                headerFilter: true,
                sorter: 'number',
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'expiryDate.label'
                    },
                    {
                      fields: 'expiryDate.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'expiryDate'
                },
                width: '8%'
              }
            },
            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                maxWidth: 85,
                width: '5%',
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'datasource-management': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Name',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'name'
                },
                width: '15%'
              },
              extras: {
                type: 'text'
              }
            },
            {
              tabulatorInfo: {
                field: 'type.label',
                title: 'Type',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'type'
                },
                width: '8%'
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'status'
                },
                width: '8%'
              },

              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'location',
                title: 'Location',
                headerFilter: true,
                formatterParams: {
                  tableName: 'location'
                },
                width: '8%'
              }
            },
            {
              tabulatorInfo: {
                field: 'mgmtIpAddress.label',
                title: 'Management IP',
                tooltip: false,
                headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'mgmtIpAddress'
                    }
                  ]
                },
                formatter: this.tabulatorService.getMatIconText,
                formatterParams: {
                  columnName: 'mgmtIpAddress'
                },
                headerFilter: true,
                sorter: 'alphanum',
                width: '10%'
              }
            },
            {
              tabulatorInfo: {
                field: 'ipAddress.label',
                title: 'Test IP',
                headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'ipAddress'
                    }
                  ]
                },
                headerFilter: true,
                sorter: 'alphanum',
                width: '10%'
              }
            },

            {
              tabulatorInfo: {
                field: 'registered.value',
                title: 'Registered',
                id: 'registered',
                headerFilter: true,
                sorter: 'number',
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'registered.label'
                    },
                    {
                      fields: 'registered.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'registered'
                },
                width: '8%'
              }
            },
            {
              tabulatorInfo: {
                field: 'version',
                title: 'Version',
                headerFilter: true,
                sorter: function (a, b) {
                  const parseVersion = version => version.split('.').map(Number); // Convert to number array

                  const v1 = parseVersion(String(a.label));
                  const v2 = parseVersion(String(b.label));

                  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
                    const num1 = v1[i] || 0; // Default to 0 if undefined
                    const num2 = v2[i] || 0;

                    if (num1 !== num2) return num1 - num2; // Compare segment-wise
                  }
                  return 0; // If all segments are equal
                },
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'version.label'
                    },
                    {
                      fields: 'version.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'version'
                },
                width: '10%'
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'testAssigned',
                title: 'Tests running',
                tooltip: false,
                headerSort: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'progress-bar',
                  columnName: 'testAssigned'
                },
                width: '10%'
              }
            },
            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: '5%',
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'endpoints-management': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Name',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name'
                    }
                  ]
                },
                width: '25%'
              },
              extras: {
                type: 'text'
              }
            },
            {
              tabulatorInfo: {
                field: 'type.label',
                title: 'Type',
                headerFilter: true,

                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'type'
                },
                width: '10%'
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'status'
                },
                width: '10%'
              },

              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'ipAddress.label',
                title: 'Address',
                headerFilterFunc: this.tabulatorService.deepMatchHeaderFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'ipAddress'
                    }
                  ]
                },
                headerFilter: true,
                sorter: 'alphanum',
                width: '15%'
              }
            },
            {
              tabulatorInfo: {
                field: 'testAssigned',
                title: 'Tests running',
                tooltip: false,
                headerSort: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'progress-bar',
                  columnName: 'testAssigned'
                },
                width: '10%'
              }
            },
            {
              tabulatorInfo: {
                field: 'reflectorData',
                title: 'Reflector Action',
                cellClick: function (e, cell) {
                  cell.getRow().treeToggle();
                  //cell - cell component
                },
                formatter: this.tabulatorService.getIconLabelText,
                width: '15%'
              }
            },
            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: '5%',
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'templates-management': {
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'name',
                title: 'Name',
                id: 'name',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'type.label',
                title: 'Type',
                id: 'type',
                tooltip: false,
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  columnName: 'tests',
                  tableName: 'tests'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'owner',
                title: 'Owner',
                id: 'owner',
                headerFilter: true
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'testType',
                title: 'testType',
                id: 'testType',
                headerFilter: true
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'accessLevel',
                title: 'Access level',
                id: 'accessLevel',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'accessLevel'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: false
              }
            },
            {
              tabulatorInfo: {
                field: 'lastModified',
                title: 'Last modified',
                id: 'lastModified',
                headerFilter: true,
                sorter: 'number',
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'lastModified.label'
                    },
                    {
                      fields: 'lastModified.sublabel'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'lastModified'
                }
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                id: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 85,
                clickMenu: []
              }
            },
            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'selectActions',
                id: 'selectActions',
                resizable: false,
                formatter: this.tabulatorService.getActionLabel,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                headerSort: false,
                tooltip: false,
                width: 150,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'device-selection': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'name.label',
                title: 'Name',
                headerFilter: true,
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'name.label'
                    }
                  ]
                },
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'name'
                }
              }
            },

            {
              tabulatorInfo: {
                field: 'type.label',
                title: 'Type',
                tooltip: false,
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'type'
                }
              },
              extras: {
                isGroupBy: true,
                isMultiselectFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                tooltip: false,
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'status'
                }
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'mgmtIp',
                title: 'Management IP',
                tooltip: false,
                headerFilter: true
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'ip',
                title: 'Test Address',
                tooltip: false,
                headerFilter: true
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                field: 'testAssigned',
                title: 'Tests running',
                tooltip: false,
                headerSort: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'progress-bar',
                  columnName: 'testAssigned'
                }
              }
            },

            {
              tabulatorInfo: {
                field: 'location',
                title: 'Location',
                tooltip: false,
                headerFilter: true,
                formatterParams: {
                  tableName: 'location'
                }
              },
              extras: {
                isGroupBy: true
              }
            },
            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getSpecsLabel,
                formatterParams: {
                  tableName: 'ActiveTests'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                maxWidth: 85,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'jmeps-discovery': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'ipAddress',
                title: 'IP Address',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'probeId',
                title: 'Probe ID',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'equipmentMac',
                title: 'Equipment MAC',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'announced',
                title: 'Announced',
                tooltip: false,
                headerFilter: false,
                visible: false,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'announced'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'activateStatus',
                title: 'Activated',
                tooltip: false,
                headerFilter: false,
                hozAlign: 'center',
                formatter: this.tabulatorService.getMatIconWithText,
                formatterParams: {
                  columnName: 'activateStatus'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'processState',
                title: 'Processed',
                tooltip: false,
                headerFilter: false,
                hozAlign: 'center',
                formatter: this.tabulatorService.getMatIconWithText,
                formatterParams: {
                  columnName: 'processState'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'ipAssignStatus',
                title: 'Auto configured',
                tooltip: false,
                headerFilter: false,
                hozAlign: 'center',
                visible: false,
                formatter: this.tabulatorService.getMatIcon,
                formatterParams: {
                  columnName: 'ipAssignStatus'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'errorMsg',
                title: 'Error',
                tooltip: false,
                headerFilter: false
              }
            }
          ],
          data: []
        };
      }
      case 'flows-selection': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'flowId',
                title: 'Flow ID',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'srcIp',
                title: 'Source IP',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'destIP',
                title: 'Dest IP',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'srcMac',
                title: 'Source MAC Address',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'destMac',
                title: 'Dest MAC Address',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'negPort',
                title: 'Dest Port',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'dscp',
                title: 'Service/DSCP',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'megLevel',
                title: 'Meg Level',
                tooltip: false,
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'nfVal',
                title: 'Near -> Far',
                tooltip: false,
                headerFilter: true,
                visible: false,
                titleFormatter: this.tabulatorService.formatHeaderWithMetrics,
                titleFormatterParams: {
                  title: 'Near -> Far',
                  subTitle: metricsUnit
                },
                formatter: this.tabulatorService.getCellCustomClass,
                formatterParams: {
                  columnName: 'nfVal'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'fnVal',
                title: 'Far -> Near',
                tooltip: false,
                headerFilter: true,
                visible: false,
                titleFormatter: this.tabulatorService.formatHeaderWithMetrics,
                titleFormatterParams: {
                  title: 'Far -> Near',
                  subTitle: metricsUnit
                },
                formatter: this.tabulatorService.getCellCustomClass,
                formatterParams: {
                  columnName: 'fnVal'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'twVal',
                title: '2-Way',
                tooltip: false,
                headerFilter: true,
                visible: false,
                titleFormatter: this.tabulatorService.formatHeaderWithMetrics,
                titleFormatterParams: {
                  title: '2-Way',
                  subTitle: metricsUnit
                },
                formatter: this.tabulatorService.getCellCustomClass,
                formatterParams: {
                  columnName: 'twVal'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'fsync',
                title: 'Fsync Lost',
                tooltip: false,
                headerFilter: true,
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'pmStats',
                title: 'PM Stats',
                tooltip: false,
                headerFilter: false,
                hozAlign: 'center',
                with: '15%',
                visible: false,
                formatter: this.tabulatorService.getPmStatsIcon,
                formatterParams: {
                  tableName: 'pmStats'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'details',
                title: 'Details',
                headerSort: false,
                tooltip: false,
                visible: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'flow-details',
                  columnName: 'details'
                },
                width: '15%'
              }
            }
          ],
          data: []
        };
      }
      case 'uploads-settings': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'image',
                title: 'Image Name',
                tooltip: false,
                headerFilter: true,
                width: '30%'
              }
            },
            {
              tabulatorInfo: {
                field: 'type',
                title: 'Type',
                tooltip: false,
                width: '25%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'version',
                title: 'Version',
                tooltip: false,
                width: '25%',
                headerFilter: true
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                id: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'uploads-settings'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 110,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'downloads-settings': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'fileName',
                title: 'File Name',
                tooltip: true,
                headerFilter: true,
                width: '30%'
              }
            },
            {
              tabulatorInfo: {
                field: 'lastAuditTime',
                title: 'Last Updated',
                tooltip: false,
                width: '18%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                tooltip: false,
                width: '15%',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'downloadsStatus'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'message',
                title: 'Status Message',
                tooltip: true,
                width: '25%',
                headerFilter: true
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                id: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'downloads-settings'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: 110,
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'uploads-jmep-mapping': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'fileName',
                title: 'File Name',
                tooltip: false,
                headerFilter: true,
                width: '40%'
              }
            },
            {
              tabulatorInfo: {
                field: 'uploadTime',
                title: 'Last Uploaded Time',
                tooltip: false,
                width: '15%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'resultsTime',
                title: 'Last Results Time',
                tooltip: false,
                width: '15%',
                headerFilter: true
              }
            },

            {
              tabulatorInfo: {
                field: 'status',
                title: 'Apply Status',
                tooltip: false,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'inProgress'
                },
                width: '20%',
                // hozAlign: 'center',
                headerFilter: true
              }
            },

            {
              tabulatorInfo: {
                title: 'Actions',
                field: 'actions',
                id: 'actions',
                resizable: false,
                formatter: this.tabulatorService.getActionIcon,
                formatterParams: {
                  tableName: 'uploads-jmep-mapping'
                },
                cssClass: 'action-menu',
                hozAlign: 'center',
                headerSort: false,
                tooltip: false,
                width: '10%',
                clickMenu: []
              }
            }
          ],
          data: []
        };
      }
      case 'uploads-view-results': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'id',
                title: 'Id',
                visible: false
              }
            },

            {
              tabulatorInfo: {
                field: 'applyTime',
                title: 'Apply Time',
                id: 'applyTime',
                headerFilter: true,
                sorter: 'number',
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'nested-icon',
                  columnName: 'applyTime'
                },
                headerFilterFunc: this.tabulatorService.customFilter,
                headerFilterFuncParams: {
                  extraValueForFilter: [
                    {
                      fields: 'applyTime.label'
                    },
                    {
                      fields: 'applyTime.sublabel'
                    }
                  ]
                },
                width: '10%'
              }
            },
            {
              tabulatorInfo: {
                field: 'model',
                title: 'Model',
                tooltip: true,
                width: '8%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'key',
                title: 'Key',
                tooltip: true,
                width: '15%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'overWrite',
                title: 'Overwrite',
                tooltip: false,
                width: '8%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'originalName',
                title: 'Original Name',
                tooltip: true,
                width: '17%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'newName',
                title: 'New Name',
                tooltip: true,
                width: '17%',
                headerFilter: true
              }
            },

            {
              tabulatorInfo: {
                field: 'status',
                title: 'Status',
                tooltip: false,
                width: '8%',
                // hozAlign: 'center',
                headerFilter: true,
                formatter: this.tabulatorService.getIcon,
                formatterParams: {
                  tableName: 'uploadsStatus'
                }
              }
            },
            {
              tabulatorInfo: {
                field: 'reason',
                title: 'Reason',
                tooltip: true,
                width: '15%',
                headerFilter: true
              }
            }
          ],
          data: []
        };
      }
      case 'agent-logs': {
        ``;
        return {
          dimension: type,
          title: '',
          column: [
            {
              tabulatorInfo: {
                field: 'maId',
                title: 'Id',
                visible: false
              }
            },
            {
              tabulatorInfo: {
                field: 'agentName',
                title: 'Agent Name',
                tooltip: false,
                width: '50%',
                headerFilter: true
              }
            },
            {
              tabulatorInfo: {
                field: 'timestamp',
                title: 'Download Logs',
                tooltip: false,
                width: '50%',
                headerFilter: false,
                headerSort: false,
                clickPopup: this.tabulatorService.cellclick,
                formatter: this.tabulatorService.getRow,
                formatterParams: {
                  type: 'timestamp-logs',
                  columnName: 'timestamp'
                }
              }
            }
          ],
          data: []
        };
      }
      default:
        return;
    }
  }
  private getTemplateActions(entity) {
    const checkRolesForActions = this.templateManagedActions(entity);
    const actions = [
      {
        id: 'open',
        option: FUSION_TEMPLATE_ACTION_NAME.OPEN,
        label: FUSION_TEMPLATE_ACTION_NAME.OPEN,
        iconName: 'mdi-clipboard-text',
        isDefaultAction: false
      },
      {
        id: 'setAsDefaults',
        option: FUSION_TEMPLATE_ACTION_NAME.SET_AS_DEFAULTS,
        label: FUSION_TEMPLATE_ACTION_NAME.SET_AS_DEFAULTS,
        iconName: 'mdi-star default-templates',
        isDefaultAction: false
      },
      {
        id: 'unsetDefaults',
        option: FUSION_TEMPLATE_ACTION_NAME.UNSET_AS_DEFAULTS,
        label: FUSION_TEMPLATE_ACTION_NAME.UNSET_AS_DEFAULTS,
        iconName: 'mdi-star-off default-templates',
        isDefaultAction: false
      },
      {
        id: 'share',
        option: FUSION_TEMPLATE_ACTION_NAME.SHARE,
        label: FUSION_TEMPLATE_ACTION_NAME.SHARE,
        iconName: 'mdi-share share-templates',
        isDefaultAction: false
      },
      {
        id: 'unshare',
        option: FUSION_TEMPLATE_ACTION_NAME.UNSHARE,
        label: FUSION_TEMPLATE_ACTION_NAME.UNSHARE,
        iconName: 'mdi-share-off share-templates',
        isDefaultAction: false
      },
      {
        id: 'remove',
        option: FUSION_TEMPLATE_ACTION_NAME.REMOVE,
        label: FUSION_TEMPLATE_ACTION_NAME.REMOVE,
        iconName: 'mdi-delete',
        isDefaultAction: false
      }
    ];

    const filterTemplateActions = filter(actions, action => {
      return checkRolesForActions.indexOf(action.id) !== -1;
    });

    return [{ label: '', actions: filterTemplateActions }];
  }

  // eslint-disable-next-line
  getEndpointsActions(entity: any): any[] {
    const actions = [
      {
        id: 'agentspecs',
        option: FUSION_AGENT_ACTION_NAME.AGENT_SPECS,
        label: FUSION_AGENT_ACTION_NAME.AGENT_SPECS,
        disable: entity.status === 'unregistered',
        iconName: 'mdi-clipboard-text',
        isDefaultAction: false
      }
    ];

    return [{ label: '', actions: actions }];
  }

  // eslint-disable-next-line
  getDeviceManagmentActions(entity: IDeviceData, isDisabled = false): any[] {
    const actions = [
      {
        id: 'agentspecs',
        option: FUSION_AGENT_ACTION_NAME.AGENT_SPECS,
        label: FUSION_AGENT_ACTION_NAME.AGENT_SPECS,
        disable: entity.status === 'unregistered',
        iconName: 'mdi-clipboard-text',
        isDefaultAction: false
      },
      {
        id: 'edit',
        option: FUSION_AGENT_ACTION_NAME.EDIT,
        label: FUSION_AGENT_ACTION_NAME.EDIT,
        disable: entity.activity.status === 'running',
        iconName: 'mdi-pencil',
        isDefaultAction: false
      },
      {
        id: 'remove',
        option: FUSION_AGENT_ACTION_NAME.REMOVE,
        label: FUSION_AGENT_ACTION_NAME.REMOVE,
        disable: !this.userAuth.isAuthedFor('READ_WRITE'),
        iconName: 'mdi-delete',
        isDefaultAction: false
      },
      {
        id: 'viewtests',
        option: FUSION_AGENT_ACTION_NAME.VIEW_TESTS,
        label: FUSION_AGENT_ACTION_NAME.VIEW_TESTS,
        disable: false,
        iconName: 'mdi-pulse',
        isDefaultAction: false
      },
      {
        id: 'viewJmeps',
        option: FUSION_AGENT_ACTION_NAME.VIEW_JMEPS,
        label: FUSION_AGENT_ACTION_NAME.VIEW_JMEPS,
        disable: false,
        iconName: 'mdi-cog',
        isDefaultAction: false
      },
      {
        id: 'discoverJmeps',
        option: FUSION_AGENT_ACTION_NAME.DISCOVER_JMEPS,
        label: FUSION_AGENT_ACTION_NAME.DISCOVER_JMEPS,
        disable: entity.status === 'offline',
        iconName: 'mdi-cast',
        isDefaultAction: false
      },
      {
        id: 'reboot',
        option: FUSION_AGENT_ACTION_NAME.REBOOT,
        label: FUSION_AGENT_ACTION_NAME.REBOOT,
        disable: !this.isRebootSupported(entity) || !this.userAuth.isAuthedFor('READ_WRITE'),
        iconName: 'mdi-rotate-left',
        isDefaultAction: false
      },
      {
        id: 'alarms',
        option: FUSION_AGENT_ACTION_NAME.ALARMS,
        label: FUSION_AGENT_ACTION_NAME.ALARMS,
        disable: false,
        iconName: 'mdi-alarm-light',
        isDefaultAction: false
      },
      {
        id: 'configure',
        option: FUSION_AGENT_ACTION_NAME.CONFIGURE,
        label: FUSION_AGENT_ACTION_NAME.CONFIGURE,
        disable: entity.status === 'offline',
        iconName: 'mdi-cog',
        isDefaultAction: false
      },
      {
        id: 'restart',
        option: FUSION_AGENT_ACTION_NAME.RESTART,
        label: FUSION_AGENT_ACTION_NAME.RESTART,
        disable: entity.status === 'offline',
        iconName: 'mdi-restart',
        isDefaultAction: false
      },
      {
        id: 'resync',
        option: FUSION_AGENT_ACTION_NAME.RESYNC,
        label: FUSION_AGENT_ACTION_NAME.RESYNC,
        disable: entity.status === 'offline',
        iconName: 'mdi-sync',
        isDefaultAction: false
      },
      {
        id: 'stopLoopback',
        option: FUSION_AGENT_ACTION_NAME.STOP,
        label: FUSION_AGENT_ACTION_NAME.STOP,
        disable: entity.status === 'offline',
        iconName: 'mdi-stop',
        isDefaultAction: false
      },
      {
        id: 'trafficResults',
        option: FUSION_AGENT_ACTION_NAME.TRAFFIC_RESULTS,
        label: FUSION_AGENT_ACTION_NAME.TRAFFIC_RESULTS,
        disable: !this.licenseService.getJmepTrafficLicense(),
        iconName: 'mdi-redo',
        isDefaultAction: false
      },
      {
        id: 'transferDevice',
        option: FUSION_AGENT_ACTION_NAME.TRANSFER_DEVICE,
        label: FUSION_AGENT_ACTION_NAME.TRANSFER_DEVICE,
        disable: false,
        iconName: 'mdi-transit-transfer',
        isDefaultAction: false
      },
      {
        id: 'upgrade',
        option: FUSION_AGENT_ACTION_NAME.UPGRADE_AGENT,
        label: FUSION_AGENT_ACTION_NAME.UPGRADE_AGENT,
        disable: !this.userAuth.isAuthedFor('READ_WRITE') || isDisabled,
        iconName: 'mdi-upload',
        isDefaultAction: false
      },
      {
        id: 'jmep-upgrade',
        option: FUSION_AGENT_ACTION_NAME.UPGRADE_JMEPS,
        label: FUSION_AGENT_ACTION_NAME.UPGRADE_JMEPS,
        disable: !this.userAuth.isAuthedFor('READ_WRITE') || isDisabled,
        iconName: 'mdi-upload',
        isDefaultAction: false
      },
      {
        id: 'logs',
        option: FUSION_AGENT_ACTION_NAME.LOGS,
        label: FUSION_AGENT_ACTION_NAME.LOGS,
        disable:
          !this.userAuth.isAuthedFor('READ_WRITE') ||
          entity?.status !== 'online' ||
          entity?.substatus === 'upgrade',
        iconName: 'mdi-file-document-plus-outline',
        isDefaultAction: false
      }
    ];
    return [{ label: '', actions: actions }];
  }

  private templateManagedActions(template) {
    const ACCESSLEVEL = {
      PRIVATE: 'private',
      SHARED: 'shared'
    };
    const templateAccessLevel = template.accessLevel;
    const templateIsDefault = template.isDefault;
    const templateOwner = template.user;
    const currentUser = this.userService.getUserName();
    const readWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
    const manageTestsRole = this.userAuth.isAuthedFor('MANAGE_TESTS');
    const manageUsersRole = this.userAuth.isAuthedFor('MANAGE_PERMISSIONS');
    const userOwnsTemplate = currentUser === templateOwner;
    const templateIsPrivate = templateAccessLevel === ACCESSLEVEL.PRIVATE;
    const templateIsShared = templateAccessLevel === ACCESSLEVEL.SHARED;
    const canManageShared = readWriteRole && templateIsShared;
    const actionsWithRoles = {
      CREATE: manageTestsRole,
      OPEN: userOwnsTemplate || templateIsShared || manageUsersRole,
      REMOVE: userOwnsTemplate || canManageShared || manageUsersRole,
      EDIT: userOwnsTemplate || canManageShared,
      SHARE: readWriteRole && userOwnsTemplate && templateIsPrivate,
      UNSHARE: canManageShared,
      SET_DEFAULTS: !templateIsDefault && canManageShared && (userOwnsTemplate || templateIsShared),
      UNSET_DEFAULTS: templateIsDefault && canManageShared
    };
    const templateManagedActionsObj = [
      actionsWithRoles['OPEN'] ? 'open' : '',
      actionsWithRoles['SHARE'] ? 'share' : '',
      actionsWithRoles['UNSHARE'] ? 'unshare' : '',
      actionsWithRoles['SET_DEFAULTS'] ? 'setAsDefaults' : '',
      actionsWithRoles['UNSET_DEFAULTS'] ? 'unsetDefaults' : '',
      actionsWithRoles['REMOVE'] ? 'remove' : ''
    ];
    return templateManagedActionsObj;
  }
  // eslint-disable-next-line
  getActionsByAgentType(actions: any, entity: IDeviceData): any {
    const actionList = filter(actions, action => {
      if (entity.model.model.indexOf('JMEP') !== -1) {
        return this.jmepActions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('vPMA') !== -1) {
        return this.vpmaActions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('ESAM') !== -1) {
        return this.esamActions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('vQT') !== -1) {
        return this.vqtActions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('QT600') !== -1) {
        return this.qt600Actions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('MAP-2100') !== -1) {
        return this.map2100Actions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('vSPA') !== -1) {
        return this.vspaActions.indexOf(action.id) !== -1;
      } else if (entity.model.model.indexOf('vTA') !== -1) {
        return this.vmaActions.indexOf(action.id) !== -1;
      } else {
        return this.commonDeviceActions.indexOf(action.id) !== -1;
      }
    });
    return actionList;
  }
  // eslint-disable-next-line
  getEsamName(entity: any, devices: Array<Device>): any {
    if (entity.model.model.indexOf('JMEP') !== -1) {
      const esamObj = devices.find(item => {
        return item.getId() === entity?.capabilities?.phy['esam-guid'];
      });
      return esamObj ? 'ESAM: ' + esamObj.getUniqueName() : '';
    } else {
      return '';
    }
  }
  private getDateTimeFormat(date: string) {
    if (date) {
      const momentDate = new Date(date);
      const timestamp = moment(momentDate).valueOf();
      return {
        label: Number.isInteger(timestamp) ? timestamp : date,
        sublabel: Number.isInteger(timestamp) ? this.tabulatorService.getTimeFormat(timestamp) : ''
      };
    }
  }
  // eslint-disable-next-line
  getISODateTimeFormat(date: string) {
    if (date) {
      const momentDate = new Date(date);
      const timestamp = moment(momentDate).valueOf();
      return {
        value: timestamp,
        label: Number.isInteger(timestamp) ? this.tabulatorService.getDateFormat(timestamp) : date,
        sublabel: Number.isInteger(timestamp) ? this.tabulatorService.getTimeFormat(timestamp) : ''
      };
    }
  }
  // eslint-disable-next-line
  capitalizeFirstLetter(word: string): string {
    if (!word) return word;
    return word[0].toUpperCase() + word.substr(1).toLowerCase();
  }
  // eslint-disable-next-line
  private isRebootSupported(entity: IDeviceData): boolean {
    const supportedType = [
      AGENT_MODEL.VQT,
      AGENT_MODEL.HARDWARE,
      this.testListTransform.getAgentModelLabel('vPMA'),
      this.testListTransform.getAgentModelLabel('vpma-l')
    ];
    return supportedType.includes(entity.model.model) && entity.status !== 'unregistered';
  }

  // eslint-disable-next-line
  getAgentTypeBasedOnPlatform(platform: string): AGENT_MODEL {
    switch (platform) {
      case 'WINDOWS':
        return AGENT_MODEL.WINDOWS;
      case 'MAC':
        return AGENT_MODEL.MAC;
      case 'LINUX':
        return AGENT_MODEL.LINUX;
      case 'VMDK':
      case 'OVA':
      case 'QCOW2':
      case 'vQT':
        return AGENT_MODEL.VQT;
      case 'BARE_METAL':
      case 'OLHW':
        return AGENT_MODEL.HARDWARE;
      case AGENT_MODEL.QT600:
        return AGENT_MODEL.QT600;
      case AGENT_MODEL.MAP2100:
        return AGENT_MODEL.MAP2100;
      case AGENT_MODEL.VSPA:
        return AGENT_MODEL.VSPA;
      case AGENT_MODEL.JMEP_10G:
        return AGENT_MODEL.JMEP_10G;
      case AGENT_MODEL.JMEP_1G:
        return AGENT_MODEL.JMEP_1G;
      case AGENT_MODEL.VSPA_PM:
        return AGENT_MODEL.VSPA_PM;
      case AGENT_MODEL.VSPA_PM_L:
        return AGENT_MODEL.VSPA_PM_L;
      case AGENT_MODEL.VSPA_SAT:
        return AGENT_MODEL.VSPA_SAT;
      case AGENT_MODEL.VSPA_SAT_LB:
        return AGENT_MODEL.VSPA_SAT_LB;
      case AGENT_MODEL.VSPA_SAT_TS:
        return AGENT_MODEL.VSPA_SAT_TS;
    }

    return null;
  }

  // eslint-disable-next-line
  getDeviceTypeInfo(agentType: string) {
    const result = {
      label: '',
      name: ''
    };
    const agentModel = this.testListTransform.getAgentModelLabel(agentType);
    result.label = agentModel;
    result.name = agentModel;
    return result;
  }

  private getTimestampAndLogURL(entity: any): any[] {
    const logs = [];
    entity.logUrls.forEach(item => {
      const formatTimestamp = this.getDateFormat(item.logURL.timestamp);
      const url = item.logURL.url;
      logs.push({ timestamp: formatTimestamp, url: url });
    });
    return logs;
  }

  private getDateFormat(date: string): string {
    const timestamp = this.dateFormatService.getTimestamp(date);
    const formatDate = this.dateFormatService.getLocaleDateString(timestamp);
    const time = this.dateFormatService.getTime(date, 'HH:mm:ss');
    return formatDate + ' ' + time;
  }
}
